import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { siteInfo } from '@/mocks/site';
import { testimonials } from '@/mocks/testimonials';

const milestones = [
  { year: '2019', title: '平台成立', desc: '蘑菇视频网页版正式上线，主打无需下载即可在线观看的便捷体验。首批上线电视剧和电影两大核心板块。' },
  { year: '2020', title: '内容扩展', desc: '新增综艺、动漫、纪录片三大板块，视频资源突破5万部。用户量突破500万，覆盖全国主要城市。' },
  { year: '2021', title: '技术升级', desc: '全面升级播放器技术，支持4K超高清画质和自适应码率。移动端网页体验大幅优化，手机浏览器观看体验媲美原生App。' },
  { year: '2022', title: '用户破亿', desc: '注册用户突破1亿，日活跃用户超过500万。新增短剧频道，短剧内容迅速成为平台增长最快的板块。' },
  { year: '2023', title: '功能完善', desc: '推出夜间模式、画中画播放、多设备同步等高级功能。个性化推荐算法上线，精准匹配用户观影偏好。' },
  { year: '2024', title: '品质升级', desc: '引入超清4K片源和杜比音效，观影品质再上新台阶。体育、音乐频道上线，内容生态更加完善。' },
  { year: '2025', title: '生态拓展', desc: '用户突破5000万，日均播放量超过1亿次。推出会员服务体系，VIP用户专享免广告、提前看等特权。' },
  { year: '2026', title: '全新篇章', desc: '平台全面升级AI智能推荐引擎，用户体验持续优化。持续引入国内外优质内容，打造全方位的在线视频生态。' },
];

export default function AboutPage() {
  const [expandedTestimonial, setExpandedTestimonial] = useState<string | null>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.moguvideo.com/',
    description: siteInfo.description,
    foundingDate: '2019',
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.moguvideo.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.moguvideo.com/about' },
    ],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蘑菇视频网页版',
    description: siteInfo.description,
    url: 'https://www.moguvideo.com/about',
  };

  return (
    <>
      <SchemaOrg schema={orgSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <SchemaOrg schema={aboutPageSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <nav className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <a href="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </div>
        </nav>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-5">
              关于蘑菇视频<span className="text-primary-500">网页版</span>
            </h1>
            <p className="text-foreground-600 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 max-w-3xl mx-auto">
              {Object.entries(siteInfo.stats).map(([key, value]) => (
                <div key={key} className="text-center p-4 rounded-xl bg-background-100">
                  <div className="text-2xl md:text-3xl font-bold text-accent-500 font-heading">{value}</div>
                  <div className="text-xs text-foreground-600 mt-1">
                    {key === 'videos' && '视频资源'}
                    {key === 'users' && '注册用户'}
                    {key === 'dailyActive' && '日活用户'}
                    {key === 'categories' && '内容分类'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-12">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-accent-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div
                    key={m.year}
                    className={`relative flex flex-col md:flex-row gap-4 pl-12 md:pl-0 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`hidden md:block flex-1 ${idx % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                      <div className={`${idx % 2 === 0 ? 'ml-auto' : 'mr-auto'} max-w-xs`}>
                        {idx % 2 === 0 ? (
                          <>
                            <div className="text-sm font-bold text-accent-500 mb-1">{m.year}</div>
                            <h3 className="text-base font-semibold text-foreground-950 mb-1">{m.title}</h3>
                            <p className="text-sm text-foreground-600">{m.desc}</p>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="absolute left-2 md:left-1/2 w-5 h-5 rounded-full bg-accent-500 border-4 border-background-100 -translate-x-1/2 z-10"></div>

                    <div className={`hidden md:block flex-1 ${idx % 2 === 0 ? 'text-left pl-8' : 'text-right pr-8'}`}>
                      <div className={`${idx % 2 !== 0 ? 'ml-auto' : 'mr-auto'} max-w-xs`}>
                        {idx % 2 !== 0 ? (
                          <>
                            <div className="text-sm font-bold text-accent-500 mb-1">{m.year}</div>
                            <h3 className="text-base font-semibold text-foreground-950 mb-1">{m.title}</h3>
                            <p className="text-sm text-foreground-600">{m.desc}</p>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="md:hidden">
                      <div className="text-sm font-bold text-accent-500 mb-1">{m.year}</div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-1">{m.title}</h3>
                      <p className="text-sm text-foreground-600">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-3">
              用户口碑
            </h2>
            <p className="text-foreground-600 text-center text-sm md:text-base mb-12">
              来自真实用户的评价与反馈
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((tm) => (
                <div
                  key={tm.id}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={tm.avatar}
                      alt={tm.name}
                      className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground-950">{tm.name}</span>
                        <span className="text-xs text-foreground-500 bg-background-200/70 px-2 py-0.5 rounded-full whitespace-nowrap">{tm.role}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {Array.from({ length: tm.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                        ))}
                      </div>
                      <p className={`text-sm text-foreground-700 leading-relaxed ${expandedTestimonial === tm.id ? '' : 'line-clamp-3'}`}>
                        {tm.content}
                      </p>
                      {tm.content.length > 80 && (
                        <button
                          onClick={() =>
                            setExpandedTestimonial(expandedTestimonial === tm.id ? null : tm.id)
                          }
                          className="text-xs text-primary-500 hover:text-primary-600 mt-1 cursor-pointer font-medium"
                        >
                          {expandedTestimonial === tm.id ? '收起' : '展开全文'}
                        </button>
                      )}
                      <span className="text-xs text-foreground-500 mt-2 block">{tm.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mb-10">
              有任何问题或建议，欢迎通过以下方式联系我们
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                  <i className="ri-mail-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <a href={`mailto:${siteInfo.contact.email}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors break-all">
                  {siteInfo.contact.email}
                </a>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                  <i className="ri-wechat-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                <span className="text-sm text-foreground-600">{siteInfo.contact.wechat}</span>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                  <i className="ri-weibo-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">官方微博</h3>
                <span className="text-sm text-foreground-600">{siteInfo.contact.weibo}</span>
              </div>
            </div>
            <p className="text-sm text-foreground-500">
              <i className="ri-time-line mr-1"></i>
              工作时间：{siteInfo.contact.workingHours}
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}